import React, { useState, useEffect } from "react";
import MUIDataTable from "mui-datatables";
import axios from "axios";
import moment from "moment";
import { Tabs, Tab } from "@material-ui/core";
import styles from "./LookaheadComponents.module.scss";
import useMediaQuery from '@mui/material/useMediaQuery';

const Lookahead = ({ date }) => {
  const isMobile = useMediaQuery('(max-width:768px)');

  const [data, setData] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const get = async () => {
      setLoadingData(true);
      try {
        const response = await axios.get("/lookahead/get", {
          params: { date: date },
        });
        if (response.status === 200) {
          setData(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch data", error);
      } finally {
        setLoadingData(false);
      }
    };

    get();
  }, [date]);

  const columns = [
    {
      name: "period",
      label: "Period",
      options: {
        filter: false,
        sortThirdClickReset: true,
        sortDirection: "desc",
        setCellProps: () => ({
          className: styles.colPaddingLeft
        }),
        setCellHeaderProps: () => ({
          className: styles.colPaddingLeft
        }),
      },
    },
    {
      name: "usep",
      label: "USEP LAR ($/MWh)",
      options: {
        customBodyRender: (value, tableMeta) => {
          const rowIndex = tableMeta.tableData.length - tableMeta.rowIndex - 1;
          const values = value.split(",");
          const valuesH = data[rowIndex]?.usep_lar_h?.split(",");
          const valuesL = data[rowIndex]?.usep_lar_l?.split(",");
          return (
            <div className={styles.cellRow}>
              {values.map((val, index) => (
                <div key={index} className={`${styles.cellItem} ${index % 2 === 0 ? styles.cellEven : styles.cellOdd}`}>
                  <div className={styles.cellIndex}>{index + 1}</div>
                  <div className={styles.cellValue}>{val}</div>
                  <div className={styles.cellValueSmall}>H: {valuesH?.[index]}</div>
                  <div className={styles.cellValueSmall}>L: {valuesL?.[index]}</div>
                </div>
              ))}
            </div>
          );
        },
        setCellProps: () => ({
          className: styles.colPaddingLeft
        }),
        setCellHeaderProps: () => ({
          className: styles.colPaddingLeft
        }),
      },
    },
    {
      name: "usep_dpr",
      label: "USEP DPR ($/MWh)",
      options: {
        customBodyRender: (value, tableMeta) => {
          const rowIndex = tableMeta.tableData.length - tableMeta.rowIndex - 1;
          const reRun = data[rowIndex]?.re_run;
          return (
            <div className={styles.cellRow}>{value} {reRun ? "(R)" : ""}</div>
          );
        },
        setCellProps: () => ({
          className: styles.colPaddingLeft
        }),
        setCellHeaderProps: () => ({
          className: styles.colPaddingLeft
        }),
      },
    },
    {
      name: "conresp",
      label: "CONRES Price LAR ($/MWh)",
      options: {
        customBodyRender: (value) => {
          const values = value.split(",");
          return (
            <div className={styles.cellRow}>
              {values.map((val, index) => (
                <div key={index} className={`${styles.cellItem} ${styles.cellItemSmall} ${index % 2 === 0 ? styles.cellEven : styles.cellOdd}`}>
                  <div className={styles.cellIndex}>{index + 1}</div>
                  <div className={styles.cellValue}>{val}</div>
                </div>
              ))}
            </div>
          );
        },
        setCellProps: () => ({
          className: styles.colPaddingLeft
        }),
        setCellHeaderProps: () => ({
          className: styles.colPaddingLeft
        }),
      },
    },
    {
      name: "conresp_dpr",
      label: "CONRES Price DPR ($/MWh)",
      options: {
        customBodyRender: (value, tableMeta) => {
          const rowIndex = tableMeta.tableData.length - tableMeta.rowIndex - 1;
          const reRun = data[rowIndex]?.re_run;
          return (
            <div className={styles.cellRow}>{value} {reRun ? "(R)" : ""}</div>
          );
        },
        setCellProps: () => ({
          className: styles.colPaddingLeft
        }),
        setCellHeaderProps: () => ({
          className: styles.colPaddingLeft
        }),
      },
    },
    {
      name: "totalLoadMW",
      label: "Total Load LAR (MW)",
      options: {
        customBodyRender: (value) => {
          const values = value.split(",");
          return (
            <div className={styles.cellRow}>
              {values.map((val, index) => (
                <div key={index} className={`${styles.cellItem} ${styles.cellItemWide} ${index % 2 === 0 ? styles.cellEven : styles.cellOdd}`}>
                  <div className={styles.cellIndex}>{index + 1}</div>
                  <div className={styles.cellValue}>{val}</div>
                </div>
              ))}
            </div>
          );
        },
        setCellProps: () => ({
          className: styles.colPaddingLeft
        }),
        setCellHeaderProps: () => ({
          className: styles.colPaddingLeft
        }),
      },
    },
    {
      name: "totalLoad_dpr",
      label: "Total Load DPR (MW)",
      options: {
        customBodyRender: (value, tableMeta) => {
          const rowIndex = tableMeta.tableData.length - tableMeta.rowIndex - 1;
          const reRun = data[rowIndex]?.re_run;
          return (
            <div className={styles.cellRow}>{value} {reRun ? "(R)" : ""}</div>
          );
        },
        setCellProps: () => ({
          className: styles.colPaddingLeft
        }),
        setCellHeaderProps: () => ({
          className: styles.colPaddingLeft
        }),
      },
    },
    {
      name: "totalCurtailedLoad",
      label: "Total Curtailed Load",
      options: { 
        customBodyRender: (value) => {
          const values = value.split(",");
          return (
            <div className={styles.cellRow}>
              {values.map((val, index) => (
                <div key={index} className={`${styles.cellItem} ${styles.cellItemSmall} ${index % 2 === 0 ? styles.cellEven : styles.cellOdd}`}>
                  <div className={styles.cellIndex}>{index + 1}</div>
                  <div className={styles.cellValue}>{val}</div>
                </div>
              ))}
            </div>
          );
        },
        setCellProps: () => ({
          className: styles.colPaddingLeft
        }),
        setCellHeaderProps: () => ({
          className: styles.colPaddingLeft
        }),
      },
    },
    {
      name: "totalCurtailedLoad_dpr",
      label: "TCL DPR",
      options: {
        customBodyRender: (value, tableMeta) => {
          const rowIndex = tableMeta.tableData.length - tableMeta.rowIndex - 1;
          const reRun = data[rowIndex]?.re_run;
          return (
            <div className={styles.cellRow}>{value} {reRun ? "(R)" : ""}</div>
          );
        },
        setCellProps: () => ({
          className: styles.colPaddingLeft
        }),
        setCellHeaderProps: () => ({
          className: styles.colPaddingLeft
        }),
      },
    },
  ];

  const options = {
    filter: false,
    search: false,
    download: false,
    print: false,
    viewColumns: false,
    pagination: false,
    selectableRows: isMobile ? 'none' : 'multiple',
    textLabels: {
      body: {
        noMatch: loadingData ? "Loading..." : "No data.",
      },
    },
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <div className={styles.compactRow}>
      <Tabs value={activeTab} onChange={handleTabChange} className={styles.activeTab}>
        <Tab label="USEP" />
        <Tab label="CONRES Price" />
        <Tab label="Total Load" />
        <Tab label="Total Curtailed Load" />
      </Tabs>
      {activeTab === 0 && (
        <MUIDataTable
          title={`MCR Lookahead Run (` + moment(date).format("DD MMM YYYY") + `)`}
          data={data}
          columns={columns.filter(col => col.name === "period" || col.name === "usep" || col.name === "usep_dpr")}
          options={options}
        />
      )}
      {activeTab === 1 && (
        <MUIDataTable
          title={`MCR Lookahead Run (` + moment(date).format("DD MMM YYYY") + `)`}
          data={data}
          columns={columns.filter(col => col.name === "period" || col.name === "conresp" || col.name === "conresp_dpr")}
          options={options}
        />
      )}
      {activeTab === 2 && (
        <MUIDataTable
          title={`MCR Lookahead Run (` + moment(date).format("DD MMM YYYY") + `)`}
          data={data}
          columns={columns.filter(col => col.name === "period" || col.name === "totalLoadMW" || col.name === "totalLoad_dpr")}
          options={options}
        />
      )}
      {activeTab === 3 && (
        <MUIDataTable
          title={`MCR Lookahead Run (` + moment(date).format("DD MMM YYYY") + `)`}
          data={data}
          columns={columns.filter(col => col.name === "period" || col.name === "totalCurtailedLoad" || col.name === "totalCurtailedLoad_dpr")}
          options={options}
        />
      )}
    </div>
  );
};

export default Lookahead;
